import { z } from 'zod';
import { addDays, startOfWeek } from './dates.js';
import { idSchema, plainDateSchema, } from './schemas.js';
import { parseRecurrence } from './recurrence.js';
export const reviewWeekSchema = plainDateSchema.refine((date) => date >= '0100-01-08' && date <= '9999-12-24');
export const reviewStartSchema = z.object({ weekStart: reviewWeekSchema });
export const reviewUpdateSchema = z
    .object({
    version: z.number().int().min(0),
    step: z.number().int().min(0).max(4),
    note: z.string().max(5000),
    finished: z.boolean().default(false),
})
    .refine((v) => !v.finished || (v.step === 4 && v.note.trim().length > 0));
export const reviewActionSchema = z
    .object({
    version: z.number().int().min(0),
    ids: z.array(idSchema).min(1).max(500),
    action: z.enum(['defer', 'drop']),
    scheduledFor: plainDateSchema.optional(),
})
    .refine((v) => v.action !== 'defer' || !!v.scheduledFor);
export const reviewSettingsSchema = z.object({ day: z.number().int().min(0).max(6).nullable() });
export function suggestedReviewWeek(today, weekStartsOn) {
    return startOfWeek(addDays(today, -1), weekStartsOn);
}
export function isReviewCandidate(task, weekEnd) {
    return (task.status === 'todo' &&
        ((!task.scheduledFor && !task.dueDate) ||
            (!!task.scheduledFor && task.scheduledFor <= weekEnd) ||
            (!!task.dueDate && task.dueDate <= weekEnd)));
}
export function isRoutine(rule) {
    return !!rule && !parseRecurrence(rule)?.until;
}
export function reviewReminderTarget(date, weekStartsOn, day) {
    const reminderDate = day === null
        ? date
        : addDays(date, -((new Date(`${date}T00:00:00Z`).getUTCDay() - day + 7) % 7));
    return { reminderDate, weekStart: suggestedReviewWeek(reminderDate, weekStartsOn) };
}
/** Historical plans are deduplicated per task/week, even after multiple moves. */
export function summariseStatistics(completions, plans, starts, historyStartedOn) {
    const weeks = starts.map((weekStart) => {
        const end = addDays(weekStart, 6);
        const planned = new Set(plans.filter((p) => p.plannedFor >= weekStart && p.plannedFor <= end).map((p) => p.taskId));
        const done = completions.filter((c) => c.completedOn >= weekStart && c.completedOn <= end);
        const plannedDone = done.filter((c) => planned.has(c.taskId)).length;
        return {
            weekStart,
            planned: planned.size,
            completed: done.length,
            plannedDone,
            ratio: weekStart >= historyStartedOn && planned.size
                ? Math.round((plannedDone / planned.size) * 100)
                : null,
        };
    });
    const grouped = new Map();
    let routine = 0;
    const first = starts[0];
    const last = starts.at(-1);
    const within = completions.filter((c) => first && last && c.completedOn >= first && c.completedOn <= addDays(last, 6));
    for (const completion of within) {
        if (isRoutine(completion.recurrenceRule)) {
            routine++;
            continue;
        }
        const group = grouped.get(completion.goalId) ?? {
            goalId: completion.goalId,
            title: completion.goalTitle,
            count: 0,
            tasks: [],
        };
        group.count++;
        group.tasks.push(completion);
        grouped.set(completion.goalId, group);
    }
    return {
        weeks,
        goals: [...grouped.values()].sort((a, b) => b.count - a.count),
        routine,
        total: within.length,
        historyStartedOn,
    };
}
//# sourceMappingURL=reviews.js.map